/**
 * On-device storage.
 *
 * Three jobs, deliberately kept in one database so a single `close()` or a
 * single version bump covers all of them:
 *
 *  - the **write queue** (`SyncQueue`'s durable backing),
 *  - **photo blobs**, held until they upload,
 *  - a **read cache** of the script and register, so a phone that loses signal
 *    in a switchroom still knows what it is meant to be testing.
 *
 * Everything is partitioned by project. A cache keyed only by list name would
 * serve MY02's register to whatever project happened to open next, which is the
 * failure the whole data layer is arranged to prevent — so the project id is
 * part of every key here too, not just in the queries upstream.
 *
 * Every read is wrapped: storage can be unavailable in a private window, or
 * evicted under pressure. A missing cache means "fetch it again", never a crash
 * on a board at two in the morning.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Mutation, QueueStore } from '../sync/queue.js';

export const DB_NAME = 'cx-platform';
export const DB_VERSION = 1;

interface CxSchema extends DBSchema {
  queue: {
    key: string;
    value: Mutation;
    indexes: { 'by-status': string };
  };
  photos: {
    key: string;
    value: PhotoRecord;
    indexes: { 'by-sync': string };
  };
  cache: {
    key: string;
    value: CacheEntry;
  };
}

export interface PhotoRecord {
  /** Same id as the mutation that will carry it, so the two stay together. */
  offlineSyncId: string;
  projectId: number;
  assetTag: string;
  shotCode: string;
  blob: Blob;
  capturedAt: string;
  /** Set once uploaded, so a retry can skip the bytes. */
  uploadedUrl?: string;
}

export interface CacheEntry {
  key: string;
  projectId: number;
  storedAt: string;
  value: unknown;
}

export async function openCxDb(name = DB_NAME): Promise<IDBPDatabase<CxSchema>> {
  return openDB<CxSchema>(name, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('queue')) {
        const q = db.createObjectStore('queue', { keyPath: 'offlineSyncId' });
        q.createIndex('by-status', 'status');
      }
      if (!db.objectStoreNames.contains('photos')) {
        const p = db.createObjectStore('photos', { keyPath: 'offlineSyncId' });
        p.createIndex('by-sync', 'offlineSyncId');
      }
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    },
  });
}

/** The durable `QueueStore`. Swap it for `MemoryQueueStore` in tests that do not need persistence. */
export class IdbQueueStore implements QueueStore {
  constructor(private readonly db: IDBPDatabase<CxSchema>) {}

  async all(): Promise<Mutation[]> {
    return this.db.getAll('queue');
  }
  async put(m: Mutation): Promise<void> {
    await this.db.put('queue', m);
  }
  async remove(offlineSyncId: string): Promise<void> {
    await this.db.delete('queue', offlineSyncId);
  }
}

export class PhotoStore {
  constructor(private readonly db: IDBPDatabase<CxSchema>) {}

  /**
   * Writes the bytes, then reads them back before admitting success.
   *
   * `put` resolving is not proof the photo survived. A device at its quota, a
   * browser evicting the origin mid-transaction, or Safari in one of its
   * stranger moods can leave a write that resolved and a record that is absent
   * or truncated. The capture screen turns a slot green on this promise, and a
   * green slot for a photo that is not there is the single most expensive lie
   * the app can tell: the engineer stops, and the shot is gone.
   *
   * One extra read per photo, against hundreds of pounds of re-access.
   */
  async put(record: PhotoRecord): Promise<void> {
    await this.db.put('photos', record);
    const back = await this.db.get('photos', record.offlineSyncId);
    if (!back) {
      throw new Error('Photo did not save to this phone — storage may be full. Do not move on from this shot.');
    }
    if (back.blob.size !== record.blob.size) {
      throw new Error(
        `Photo saved incompletely (${back.blob.size} of ${record.blob.size} bytes). Storage is probably full.`,
      );
    }
  }
  async get(offlineSyncId: string): Promise<PhotoRecord | undefined> {
    return this.db.get('photos', offlineSyncId);
  }
  async forProject(projectId: number): Promise<PhotoRecord[]> {
    return (await this.db.getAll('photos')).filter((p) => p.projectId === projectId);
  }
  /** Photos still holding bytes. These are what the sync screen counts. */
  async pending(projectId: number): Promise<PhotoRecord[]> {
    return (await this.forProject(projectId)).filter((p) => !p.uploadedUrl);
  }
  async markUploaded(offlineSyncId: string, url: string): Promise<void> {
    const existing = await this.get(offlineSyncId);
    if (!existing) return;
    await this.db.put('photos', { ...existing, uploadedUrl: url });
  }
  /**
   * Drops the bytes of photos that have uploaded, keeping the record.
   *
   * A phone holding 8,000 full-resolution photos runs out of space long before
   * the job finishes. The record stays so the app still knows the shot was taken;
   * only the blob goes, and only once the server has it.
   */
  async reclaim(projectId: number): Promise<number> {
    const done = (await this.forProject(projectId)).filter((p) => p.uploadedUrl && p.blob.size > 0);
    for (const p of done) {
      await this.db.put('photos', { ...p, blob: new Blob([]) });
    }
    return done.length;
  }
  async bytesHeld(projectId: number): Promise<number> {
    return (await this.forProject(projectId)).reduce((n, p) => n + p.blob.size, 0);
  }
}

/** A project-partitioned read cache. Keys are never shared between projects. */
export class ReadCache {
  constructor(
    private readonly db: IDBPDatabase<CxSchema>,
    private readonly projectId: number,
  ) {
    if (!Number.isInteger(projectId) || projectId <= 0) {
      throw new Error(`a read cache needs a real project id, got ${String(projectId)}`);
    }
  }

  private key(name: string): string {
    return `${this.projectId}:${name}`;
  }

  async get<T>(name: string, maxAgeMs?: number): Promise<T | undefined> {
    try {
      const hit = await this.db.get('cache', this.key(name));
      if (!hit) return undefined;
      // Belt and braces: the key is already partitioned, but a stored row that
      // claims another project is corrupt and must not be served.
      if (hit.projectId !== this.projectId) return undefined;
      if (maxAgeMs !== undefined && Date.now() - Date.parse(hit.storedAt) > maxAgeMs) return undefined;
      return hit.value as T;
    } catch {
      return undefined; // storage unavailable — behave as a cache miss
    }
  }

  async set(name: string, value: unknown): Promise<void> {
    try {
      await this.db.put('cache', {
        key: this.key(name),
        projectId: this.projectId,
        storedAt: new Date().toISOString(),
        value,
      });
    } catch {
      /* a cache that cannot write is still a working app */
    }
  }

  /** Forgets everything cached for this project, leaving other projects alone. */
  async clear(): Promise<number> {
    const all = await this.db.getAll('cache');
    const mine = all.filter((e) => e.projectId === this.projectId);
    for (const e of mine) await this.db.delete('cache', e.key);
    return mine.length;
  }
}

/**
 * The project list, cached outside any project's partition.
 *
 * Everything else offline hangs off a chosen project, and the project list is
 * what lets you choose one. Without it cached, a phone whose sign-in lapsed
 * underground cannot get past the first screen to reach the register it already
 * holds — the data is all there and unreachable.
 */
const PROJECT_LIST_KEY = '0:projects';

export interface CachedProjects<T> {
  storedAt: string;
  value: T;
}

export interface LocalStore {
  db: IDBPDatabase<CxSchema>;
  queue: IdbQueueStore;
  photos: PhotoStore;
  cacheFor(projectId: number): ReadCache;
  /** Remembers the project list so the picker works with no signal. */
  rememberProjects(value: unknown): Promise<void>;
  recallProjects<T>(): Promise<CachedProjects<T> | undefined>;
  close(): void;
}

export async function openLocalStore(name = DB_NAME): Promise<LocalStore> {
  const db = await openCxDb(name);
  return {
    db,
    queue: new IdbQueueStore(db),
    photos: new PhotoStore(db),
    cacheFor: (projectId: number) => new ReadCache(db, projectId),
    async rememberProjects(value: unknown) {
      try {
        await db.put('cache', { key: PROJECT_LIST_KEY, projectId: 0, storedAt: new Date().toISOString(), value });
      } catch {
        /* a cache that cannot write is still a working app */
      }
    },
    async recallProjects<T>() {
      try {
        const hit = await db.get('cache', PROJECT_LIST_KEY);
        return hit ? { storedAt: hit.storedAt, value: hit.value as T } : undefined;
      } catch {
        return undefined;
      }
    },
    close: () => db.close(),
  };
}
